import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaUsers, FaPercent, FaSwimmingPool, FaWifi, FaUtensils, FaFire, FaGamepad, FaSnowflake, FaBed, FaHotTub, FaTv, FaWalking } from 'react-icons/fa';

const Services: React.FC = () => {
    const roomTypes = [
        {
            name: 'Suite Room',
            originalPrice: 6000,
            price: 6000,
            description: 'Luxurious suite with premium amenities and spacious layout perfect for couples and small families',
            features: [
                'King Size Bed with Premium Mattress',
                'Private Balcony with Garden View',
                'Premium Air Conditioning',
                'Mini Fridge & Coffee Maker',
                'Spacious Bathroom with Hot Water',
                'Free WiFi & Cable TV',
                'Room Service Available',
                'Daily Housekeeping'
            ],
            image: '/images/IMG_20251210_175845.jpg',
            capacity: '2-3 Guests',
            size: '400 sq ft'
        },
        {
            name: 'Single Room',
            originalPrice: 2500,
            price: 2500,
            description: 'Comfortable single occupancy room with all essential amenities for solo travelers and couples',
            features: [
                'Queen Size Bed',
                'Air Conditioning',
                'Free WiFi Access',
                'Hot Water 24/7',
                'Cable Television',
                'Attached Bathroom',
                'Daily Housekeeping',
                'Room Service on Request'
            ],
            image: '/images/IMG_20251210_180546.jpg',
            capacity: '1-2 Guests',
            size: '250 sq ft'
        }
    ];

    const amenities = [
        { icon: FaSwimmingPool, name: 'Unlimited Pool Access', description: 'Crystal-clear swimming pool open 24/7' },
        { icon: FaFire, name: 'Campfire Sessions', description: 'Evening campfire with music and snacks' },
        { icon: FaGamepad, name: 'Kids Play Area', description: 'Safe and fun playground for children' },
        { icon: FaSnowflake, name: 'Air Conditioning', description: 'Climate control in all rooms' },
        { icon: FaWifi, name: 'Free WiFi', description: 'High-speed internet throughout resort' },
        { icon: FaBed, name: 'Extra Bedding', description: 'Additional beds available on request' },
        { icon: FaHotTub, name: 'Hot Water Supply', description: '24/7 hot water in all bathrooms' },
        { icon: FaUtensils, name: 'Food Delivery', description: 'Curated menu from partner restaurants' },
        { icon: FaTv, name: 'Cable Television', description: 'Entertainment channels in every room' },
        { icon: FaWalking, name: 'Nature Walks', description: 'Scenic walking trails through gardens' }
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl font-bold mb-4">Rooms & Pricing</h1>
                    <p className="text-xl opacity-90 max-w-2xl mx-auto">
                        Choose from our comfortable accommodations designed for your perfect stay
                    </p>
                </div>
            </section>

            <div className="container mx-auto px-4 py-16">

                {/* Room Types */}
                <section className="mb-16">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">Our Room Types</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Each room is thoughtfully designed with modern amenities and comfort in mind
                        </p>
                    </div>

                    <div className="space-y-16">
                        {roomTypes.map((room, index) => (
                            <div key={index} className={`bg-white rounded-3xl shadow-large overflow-hidden hover:shadow-xl transition-all duration-300 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex`}>
                                {/* Image Section */}
                                <div className="lg:w-1/2 relative">
                                    <div className="h-80 lg:h-full overflow-hidden">
                                        <img 
                                            src={room.image} 
                                            alt={room.name}
                                            className="w-full h-full hover:scale-110 transition-transform duration-700 object-cover"
                                            style={room.name === 'Single Room' ? { objectPosition: '50% 80%' } : {}}
                                        />
                                    </div>

                                </div>
                                
                                {/* Content Section */}
                                <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                                    {/* Room Info */}
                                    <div className="mb-8">
                                        <h3 className="text-4xl font-bold text-text mb-4">{room.name}</h3>
                                        <div className="flex items-center space-x-6 text-gray-600 mb-6">
                                            <span className="flex items-center">
                                                <FaUsers className="mr-2 text-primary" /> {room.capacity}
                                            </span>
                                            <span className="flex items-center">
                                                <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                                                {room.size}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 text-lg leading-relaxed">{room.description}</p>
                                    </div>

                                    {/* Pricing */}
                                    <div className="mb-8">
                                        <div className="flex items-center space-x-4 mb-4">
                                            <div className="flex items-center space-x-3">
                                                <span className="text-5xl font-bold text-primary">₹{room.price.toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <p className="text-gray-600 mb-2">per night</p>
                                    </div>

                                    {/* Features */}
                                    <div className="mb-8">
                                        <h4 className="font-bold text-text mb-4 text-lg">Room Features:</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {room.features.map((feature, idx) => (
                                                <div key={idx} className="flex items-center space-x-3">
                                                    <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                                                    <span className="text-gray-700">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Book Button */}
                                    <Link 
                                        to="/book-room" 
                                        className="bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white py-4 px-8 rounded-full font-bold text-lg transition-all duration-300 text-center inline-block shadow-large hover:shadow-xl transform hover:-translate-y-1"
                                    >
                                        Book This Room
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Amenities */}
                <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">Included Amenities</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            All our rooms come with these fantastic amenities at no extra cost
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 px-8">
                        {amenities.map((amenity, index) => (
                            <div key={index} className="bg-white p-6 rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 text-center group hover:-translate-y-1">
                                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <amenity.icon className="text-2xl text-white" />
                                </div>
                                <h3 className="font-semibold text-text mb-2">{amenity.name}</h3>
                                <p className="text-sm text-gray-600 leading-relaxed">{amenity.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Booking CTA */}
                <section className="py-16 text-center">
                    <div className="bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-12 text-white">
                        <h2 className="text-4xl font-bold mb-4">Ready to Book Your Stay?</h2>
                        <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                            Experience comfort, luxury, and nature at Sunrise Agri Resorts. Book now and create unforgettable memories.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/book-room" 
                                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-medium hover:shadow-large"
                            >
                                Book Now
                            </Link>
                            <Link 
                                to="/contact" 
                                className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300"
                            >
                                Have Questions?
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Services;